import { Dialog } from '@mui/material';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { toastifyService } from '@services/ToastifyService';
import { apiErrorMessage } from '@utils/apiError';

interface BaseEditDialogProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => Promise<void> | void;
  children: React.ReactNode;
  defaultErrorMessage?: string;
  clearErrorOn?: any[];
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

export const BaseEditDialog: React.FC<BaseEditDialogProps> = ({
  title,
  isOpen,
  onClose,
  onSave,
  defaultErrorMessage = 'There was an error saving changes',
  clearErrorOn = [],
  children,
  fullWidth = false,
  maxWidth = false,
}) => {
  const handleApiError = (e: any, defaultErrorMessage: string) => {
    console.error(e);
    // Show what actually went wrong inline, not just a toast that has already gone
    const message = apiErrorMessage(e, defaultErrorMessage);
    if (message !== defaultErrorMessage) toastifyService.showError(message);
    return message;
  };

  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
  }, clearErrorOn);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await onSave();
      onClose();
    } catch (e: any) {
      setError(handleApiError(e, defaultErrorMessage));
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      disableEnforceFocus={false}
      disableAutoFocus={false}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {children}
        {error && (
          <Typography color="error" fontSize={11} style={{ alignSelf: 'center' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} size="small" style={{ fontSize: 11 }}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" size="small" style={{ fontSize: 11 }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
